const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const firstValue = (...values) => values.find(hasValue) ?? "";

const partyByRole = (deal, role) =>
  (deal?.parties || []).find((party) => party.role === role)?.legal_name || "";

const joinParts = (...parts) => parts.filter(hasValue).map((part) => String(part).trim()).join(" ");

function canonicalDealValues(deal) {
  const parameters = deal?.parameters || {};
  const goods = parameters.goods || parameters.product || {};
  const delivery = parameters.delivery || parameters.logistics || {};
  const payment = parameters.payment || {};
  const buyer = firstValue(partyByRole(deal, "buyer"), deal?.counterparty);
  const seller = partyByRole(deal, "seller");
  const bank = partyByRole(deal, "bank");
  const commodity = firstValue(goods.commodity, goods.product);
  const quantity = joinParts(goods.quantity, goods.unit);
  const currency = firstValue(deal?.currency, goods.currency, payment.currency);
  const unitPrice = joinParts(currency, goods.unit_price, goods.price_basis);
  const incoterms = delivery.incoterms || "";
  const namedDelivery = joinParts(incoterms, delivery.named_place);
  const dealSummary = [
    joinParts(deal?.reference, deal?.title),
    commodity && joinParts(commodity, quantity),
    namedDelivery,
    deal?.description,
  ].filter(hasValue).join(" — ");

  return {
    buyer,
    seller,
    bank,
    commodity,
    quantity,
    currency,
    unitPrice,
    incoterms,
    namedDelivery,
    dealSummary,
    goods,
    delivery,
    payment,
  };
}

const FIELD_RESOLVERS = {
  buyer_name: (c) => c.buyer,
  buyer: (c) => c.buyer,
  applicant: (c) => c.buyer,
  client_name: (c) => c.buyer,
  seller_name: (c) => c.seller,
  seller: (c) => c.seller,
  beneficiary: (c) => c.seller,
  counterparty_name: (c) => firstValue(c.seller, c.buyer),
  counterparty_role: (_c, deal) => {
    const role = (deal?.parties || []).find((party) => party.legal_name)?.role || "";
    return role ? `${role.charAt(0).toUpperCase()}${role.slice(1)}` : "";
  },
  issuing_bank: (c) => firstValue(c.payment.issuing_bank, c.bank),
  advising_bank: (c) => c.payment.advising_bank,
  bank_name: (c) => firstValue(c.payment.issuing_bank, c.bank),
  commodity: (c) => c.commodity,
  cargo: (c) => c.commodity,
  grade_spec: (c) => firstValue(c.goods.specification, c.goods.grade_spec, c.goods.description),
  quantity: (c) => c.quantity,
  price: (c) => c.unitPrice,
  target_price: (c) => firstValue(c.unitPrice, c.namedDelivery),
  amount: (c, deal) => firstValue(c.payment.amount, deal?.total_value),
  currency: (c) => c.currency,
  unit_price: (c) => c.unitPrice,
  named_place: (c) => c.delivery.named_place,
  deal_reference: (_c, deal) => deal?.reference,
  seller_address: (_c, deal) => (deal?.parties || []).find((p) => p.role === "seller")?.address,
  buyer_address: (_c, deal) => (deal?.parties || []).find((p) => p.role === "buyer")?.address,
  country_of_origin: (c) => firstValue(c.goods.country_of_origin, c.goods.origin),
  quantity_tolerance: (c) => c.goods.quantity_tolerance,
  incoterms: (c) => c.incoterms,
  load_port: (c) => firstValue(c.delivery.loading_port, c.delivery.load_port),
  discharge_port: (c) => c.delivery.discharge_port,
  delivery_period: (c) => firstValue(c.delivery.shipment_window, c.delivery.shipment_period),
  shipment_period: (c) => firstValue(c.delivery.shipment_window, c.delivery.shipment_period),
  latest_shipment: (c) => firstValue(c.delivery.latest_shipment, c.delivery.shipment_window),
  payment_terms: (c) => joinParts(c.payment.method, c.payment.schedule, c.payment.balance),
  payment_mode: (c) => firstValue(c.payment.payment_mode, c.payment.schedule),
  expiry_date: (c) => firstValue(c.payment.expiry_date, c.payment.validity),
  validity: (c) => c.payment.validity,
  deal_summary: (c) => c.dealSummary,
  owner: (_c, deal) => partyByRole(deal, "shipowner"),
  charterer: (_c, deal) => partyByRole(deal, "charterer"),
  broker: (_c, deal) => partyByRole(deal, "broker"),
  vessel_name: (c) => firstValue(c.delivery.vessel_name, c.delivery.vessel),
  port: (c) => firstValue(c.delivery.loading_port, c.delivery.discharge_port),
};

function exactNestedValue(deal, fieldName) {
  for (const section of Object.values(deal?.parameters || {})) {
    if (section && typeof section === "object" && hasValue(section[fieldName])) {
      return section[fieldName];
    }
  }
  return "";
}

export function buildDealTemplatePrefill(template, deal) {
  const values = {};
  let filled = 0;
  if (!template || !deal) return { values, filled };

  const canonical = canonicalDealValues(deal);
  for (const section of template.sections || []) {
    for (const field of section.fields || []) {
      const key = `${section.key}_${field.name}`;
      const exact = exactNestedValue(deal, field.name);
      const mapped = FIELD_RESOLVERS[field.name]?.(canonical, deal) ?? "";
      // Quantity is intentionally composed from the canonical amount + unit.
      // Other fields preserve an explicit template-specific value when present.
      const resolved = field.name === "quantity" ? firstValue(mapped, exact) : firstValue(exact, mapped);
      values[key] = resolved;
      if (hasValue(resolved)) filled += 1;
    }
  }
  return { values, filled };
}
